import { useCallback, useEffect, useState } from "react";

import { Navigate, useOutletContext } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";

import {
  Box,
  Container,
  Grid2 as Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  LinearProgress,
} from "@mui/material";
import { Download } from "@phosphor-icons/react";

import { Baptism, Registration } from "baptize-america-client";
import DebouncedButton from "../../components/DebounceButton";
import { LoginWindow } from "../../components/LoginWindow";
import TopNavBar from "../../components/TopNavBar";
import { getBaptism, getChurch } from "../../utils/client";
import {
  downloadCSV,
  getChurchId,
  humaneReadableTime,
  isAdmin,
  isAuthenticated,
  isChurchManager,
  isManagerRole,
} from "../../utils/helper";
import { useUserData } from "../../components/DataProvider";

const styles = {
  mainBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  headerText: {
    textAlign: "center",
    mt: 1,
    mb: 1,
    fontWeight: "bold",
    fontSize: "2rem",
    fontFamily: "AmericanGrotesk-Bold",
  },
  innerBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    mt: 2,
    width: "100%",
  },
  card: {
    p: 2,
    mb: 2,
    borderRadius: 4,
    border: 1,
    borderColor: "#CCCCCC",
    width: "100%",
  },
  attendee: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 1,
    mb: 1,
  },
  listContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 2, // Adding gap between child items
    flex: 1,
    mb: 1,
    mt: 1,
  },
  cardHeader: {
    color: "#0103077F",
  },
  nameContainer: {
    flex: 0.45,
  },
  emailContainer: {
    flex: 0.55,
  },
  attendeeName: {
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    overflowX: "auto",
  },
  attendeeEmail: {
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    overflowX: "auto",
  },
  grayText: { color: "#0103077F" },
};

type Attendees = {
  [baptismId: string]: Registration[] | [];
};

const AttendeesPage = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [churchId] = useOutletContext<(string | undefined)[]>();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { attributes } = useUserData();
  const [churchName, setChurchName] = useState<string | undefined>();
  const [baptismEvents, setBaptismEvents] = useState<Baptism[]>();
  const [attendees, setAttendees] = useState<Attendees>();
  const [isLoading, setIsLoading] = useState(true);

  const handleGetAttendees = useCallback(() => {
    getChurch({ churchId }).then(
      (res) => {
        setChurchName(res.name);
        setBaptismEvents(res.baptisms);
        if (!res.baptisms || !res.baptisms.length) return;
        const allAttendees: Attendees = {};
        const baptismPromises = res.baptisms
          .map((event) => event.baptismId)
          .filter((baptismId) => baptismId)
          .map((baptismId) => {
            return getBaptism({
              churchId: churchId,
              baptismId: baptismId,
            }).then((res) => {
              if (baptismId) {
                allAttendees[baptismId] = res.registrations ?? [];
              }
            });
          });
        Promise.all(baptismPromises)
          .then(() => {
            setAttendees(allAttendees);
          })
          .catch((error) => {
            console.error("Error occurred during getBaptism calls:", error);
          });
        setIsLoading(false);
      },
      (fail) => {
        setIsLoading(false);
        setBaptismEvents(undefined);
        setAttendees(undefined);
        console.log(`Failed to get attendees, ${fail}`);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  useEffect(() => {
    if (
      churchId &&
      (isChurchManager(attributes, churchId) || isAdmin(attributes))
    ) {
      handleGetAttendees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId, attributes]);

  const AttendeesInfo = useCallback(() => {
    return (
      <Grid container direction="row" spacing={1} sx={{ width: "100%" }}>
        {attendees &&
          (baptismEvents ?? []).map((baptism, index) => {
            if (!baptism) return null;
            const { baptismId = "" } = baptism;
            const attendeesList = Object.values(attendees[`${baptismId}`]);
            return (
              <Grid
                size={4}
                component="div"
                sx={styles.innerBox}
                key={`baptism_event_${index + 1}_attendees_${baptismId}`}
              >
                <Grid
                  component="div"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                  key={`baptism_event_time_${baptismId}`}
                >
                  <Typography
                    sx={{ fontSize: "1.3rem", pl: 1, whiteSpace: "pre-line" }}
                  >
                    {isLargeScreen
                      ? humaneReadableTime(baptism.time!!, baptism.timezone!!)
                      : humaneReadableTime(
                          baptism.time!!,
                          baptism.timezone!!,
                        ).replace("@", "\n@")}
                  </Typography>
                  <DebouncedButton
                    disabled={(attendeesList?.length ?? 0) === 0}
                    startIcon={<Download weight={"fill"} />}
                    onClick={() =>
                      downloadCSV(
                        attendeesList,
                        humaneReadableTime(baptism.time!!, baptism.timezone!!),
                      )
                    }
                  >
                    Export to CSV
                  </DebouncedButton>
                </Grid>
                <Grid
                  size={4}
                  component="div"
                  sx={[styles.card]}
                  key={`grid_attendees_list_${baptismId}`}
                >
                  {attendeesList.length ? (
                    <div key={`attendees_list_${baptismId}_${index + 1}`}>
                      <Grid
                        component="div"
                        sx={styles.listContainer}
                        key={`attendees_grid_column_headers_${baptismId}`}
                      >
                        <Typography
                          sx={[styles.nameContainer, styles.cardHeader]}
                        >
                          {"Name"}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography
                          sx={[styles.emailContainer, styles.cardHeader]}
                        >
                          {"Email"}
                        </Typography>
                      </Grid>
                      <Divider orientation="horizontal" />
                    </div>
                  ) : (
                    <div key={`no_attendees_list_${baptismId}_${index + 1}`}>
                      <Typography
                        sx={[styles.grayText, { fontStyle: "italic" }]}
                      >
                        {"No Registrations"}
                      </Typography>
                    </div>
                  )}
                  {attendeesList.map((attendee, index) => (
                    <div key={`attendees_info_grid_${baptismId}_${index + 1}`}>
                      <Grid
                        component={"div"}
                        sx={styles.listContainer}
                        key={`baptism_event_${baptismId}_attendees`}
                      >
                        <Typography
                          sx={[styles.nameContainer, styles.attendeeName]}
                        >
                          {`${attendee.firstName} ${attendee.lastName}`}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography
                          sx={[styles.emailContainer, styles.attendeeEmail]}
                        >
                          {attendee.email}
                        </Typography>
                      </Grid>
                      {attendeesList.length < index + 1 ? (
                        <Divider orientation="horizontal" sx={{ mb: 1 }} />
                      ) : null}
                    </div>
                  ))}
                </Grid>
              </Grid>
            );
          })}
      </Grid>
    );
  }, [attendees, baptismEvents, isLargeScreen]);

  return isAuthenticated(authStatus) &&
    (isChurchManager(attributes, churchId) ||
      (isAdmin(attributes) && churchId)) ? (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.mainBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar showBackArrow={false} />
        {isLoading ? (
          <Box sx={{ width: "100%", display: "block" }}>
            <LinearProgress />
          </Box>
        ) : (
          <>
            <Typography sx={[styles.headerText, { whiteSpace: "pre-line" }]}>
              {churchName}
              {"\n"}Attendees
            </Typography>
            <AttendeesInfo />
          </>
        )}
      </Box>
    </Container>
  ) : isAuthenticated(authStatus) && isManagerRole(attributes) ? (
    //Redirect since user is not manager of current route's churchId
    <Navigate to={`/attendees/${getChurchId(attributes)}`} />
  ) : isAuthenticated(authStatus) ? (
    <Navigate to={"/churches"} />
  ) : (
    <LoginWindow />
  );
};

export default AttendeesPage;
